/**
 * What a new live session is made from, checked the same way on both sides:
 * the lengths the host picks from, start times on the quarter hour, enough
 * notes to write a page from, and the one file that may come with them.
 */

/** How long a live runs, as the entry screen offers it. */
export const LIVE_LENGTHS: ReadonlyArray<{ min: number; label: string }> = [
  { min: 30, label: "30 min" },
  { min: 45, label: "45 min" },
  { min: 60, label: "1 hour" },
  { min: 90, label: "90 min" },
  { min: 120, label: "2 hours" },
];

export const LIVE_DEFAULT_LENGTH_MIN = 60;

/** Start times are offered every this many minutes. */
export const LIVE_TIME_STEP_MIN = 15;

/** A page cannot be written from less than a sentence. */
const LIVE_NOTES_MIN_WORDS = 4;
export const LIVE_NOTES_MAX_CHARS = 6000;

/** The notes or slides file, at most. */
export const LIVE_FILE_MAX_BYTES = 10 * 1024 * 1024;

type LiveFileKind = "pdf" | "text";

/** A PDF or a plain text file, read off the type and then the name; null for anything else. */
export function liveFileKind(name: string, type: string): LiveFileKind | null {
  const mime = type.toLowerCase().split(";")[0].trim();
  if (mime === "application/pdf") return "pdf";
  if (mime === "text/plain" || mime === "text/markdown") return "text";
  if (/\.pdf$/i.test(name)) return "pdf";
  if (/\.(txt|md|markdown)$/i.test(name)) return "text";
  return null;
}

/** Is there at least a sentence to write the page from? */
export function notesEnough(notes: string): boolean {
  const words = notes.trim().split(/\s+/).filter(Boolean);
  return words.length >= LIVE_NOTES_MIN_WORDS;
}

/** Is this length one the entry screen offers? */
export function isLiveLength(min: number): boolean {
  return LIVE_LENGTHS.some((l) => l.min === min);
}

/**
 * The lengths a change of time offers: the entry's, and the length it runs now when that is not one of them (a
 * 48 minute series keeps "48 min"), so keeping its length is always a choice and never reads "Select…".
 */
export function liveLengthChoices(current: number): ReadonlyArray<{ min: number; label: string }> {
  if (isLiveLength(current) || !Number.isInteger(current) || current <= 0) return LIVE_LENGTHS;
  return [...LIVE_LENGTHS, { min: current, label: `${current} min` }].toSorted((a, b) => a.min - b.min);
}

/** "19:15" is a start time the entry offers; "19:10" is not. */
export function onTheQuarter(timeHm: string): boolean {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeHm.trim());
  return !!m && Number(m[2]) % LIVE_TIME_STEP_MIN === 0;
}
